import { FC, useMemo, useState } from "react";

import { Campaign } from "@vviinn/user-managment-client-ts";

import { useAppSelector } from "../../../store";

import { VpdReferenceSiteWrapper } from "../../components";
import { AcceptableNamedRow } from "../../../ui/layout/organisms/SlideOver/SlideOverFixedLayouts/AcceptableNamedRow";
import { getReferenceSiteName } from "../../helpers/getReferenceSiteName";
import { AcceptableWithTwoProps } from "../../../helpers/functions/types";

type ReferenceCatalogModalProps = AcceptableWithTwoProps<
  Campaign["referenceSiteId"],
  Campaign["referenceCompanyId"]
> & {
  campaign: Campaign;
};

export const ReferenceCatalogAcceptableRow: FC<
  React.PropsWithChildren<ReferenceCatalogModalProps>
> = ({ onAccept, campaign }) => {
  const companyId = useAppSelector((state) => state?.company?.company?.id);

  const defaultReferenceSiteId = useAppSelector(
    (state) => state.sites.referenceSiteId?.referenceSiteId,
  );

  const { accessibleCompanies } = useAppSelector((state) => state.sites);

  const initialSiteId = useMemo(
    () =>
      campaign.referenceSiteId === null
        ? campaign.siteId
        : campaign.referenceSiteId,
    [campaign.referenceSiteId, campaign.siteId],
  );

  const initialCompanyId = useMemo(
    () =>
      campaign.referenceCompanyId === null
        ? companyId
        : campaign.referenceCompanyId,
    [campaign.referenceCompanyId, companyId],
  );

  const [selectedSiteId, setSelectedSiteId] =
    useState<Campaign["referenceSiteId"]>(initialSiteId);
  const [selectedCompanyId, setSelectedCompanyId] =
    useState<Campaign["referenceCompanyId"]>(initialCompanyId);

  const [customCatalogEnabled, setCustomCatalogEnabled] = useState<boolean>(
    campaign.referenceSiteId !== defaultReferenceSiteId,
  );

  const handleAcceptCatalogChange = async () => {
    return await onAccept(selectedSiteId, selectedCompanyId);
  };

  const selectCompanyAndSite = (custom: boolean) => {
    if (!campaign.siteId && !companyId) return;
    setSelectedCompanyId(custom ? companyId : "");
    setSelectedSiteId(custom ? campaign.siteId : defaultReferenceSiteId);
  };

  const initConfig = () => {
    setSelectedCompanyId(initialCompanyId);
    setSelectedSiteId(initialSiteId);
    setCustomCatalogEnabled(
      campaign.referenceSiteId !== defaultReferenceSiteId,
    );
  };

  return (
    <AcceptableNamedRow
      onSubmit={handleAcceptCatalogChange}
      title="reference catalog"
      permissionsGroup="campaign:advanced_config"
      initConfig={initConfig}
      allowSubmit={selectedSiteId !== initialSiteId}
      content={getReferenceSiteName(
        campaign,
        accessibleCompanies,
        companyId,
        defaultReferenceSiteId,
      )}
    >
      <VpdReferenceSiteWrapper
        selectedCompanyId={selectedCompanyId}
        selectedSiteId={selectedSiteId}
        setSelectedCompanyId={setSelectedCompanyId}
        setSelectedSiteId={setSelectedSiteId}
        active={customCatalogEnabled}
        toggleFields={(checked: boolean) => {
          setCustomCatalogEnabled(checked);
          selectCompanyAndSite(checked);
        }}
      />
    </AcceptableNamedRow>
  );
};
